import type { DocumentUploadSdkConfig, Maybe } from "@highnoteplatform/types";
import { type DocumentType as UploadDocumentType } from "@highnoteplatform/internal-sdk";

import { fetchUploadLink } from "./getUploadLink";
import { generateGenericDocumentUploadError, mapError } from "./errorHandler";
import reloadDom from "./createInnerDom";
import { fetchUploadStatus } from "./getUploadStatus";

const UPLOAD_REFRESH_INTERVAL = 10_000;
const UPLOAD_REFRESH_RETRY = 5;

const sleep = async (ms: number = UPLOAD_REFRESH_INTERVAL): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

const poll = async (
  retry: number,
  uploadedDocumentStatus: string,
  config: DocumentUploadSdkConfig,
  uploadLink: string
): Promise<string> => {
  if (retry > 0 && uploadedDocumentStatus === "PENDING") {
    await sleep();
    const status = await fetchUploadStatus(config, uploadLink);
    return status !== undefined && status !== "PENDING"
      ? status
      : await poll(retry - 1, uploadedDocumentStatus, config, uploadLink);
  }
  return uploadedDocumentStatus;
};

export const buildComponents = async (
  config: DocumentUploadSdkConfig
): Promise<Maybe<HTMLDivElement>> => {
  const { onError } = config;
  const reloadedElements = await reloadDom(config);

  const innerDom = document.createElement("div");
  innerDom.setAttribute("id", "inner-document-upload-component");

  if (reloadedElements !== undefined) {
    innerDom.append(reloadedElements);
    // add upload button if documents are required
    const fileUpload = reloadedElements.querySelector("#file-upload-component");
    if (fileUpload) {
      const uploadButton = document.createElement("button");
      uploadButton.setAttribute("id", "document-upload-button");
      const uploadButtonText = document.createTextNode("Upload");
      uploadButton.append(uploadButtonText);

      const stagnantElements = document.createElement("div");
      stagnantElements.setAttribute("id", "stagnant-document-upload-component");
      stagnantElements.append(uploadButton);

      uploadButton.addEventListener("click", async () => {
        // upload button action needs to re-query the reloaded file upload element
        const fileUploadElement = reloadedElements.querySelector(
          "#file-upload-component"
        );

        if (!(fileUploadElement instanceof HTMLInputElement)) {
          mapError(
            generateGenericDocumentUploadError(
              "Unable to get file upload element"
            ),
            onError
          );
          return;
        }
        const filesToUpload = fileUploadElement.files;

        const documentType: HTMLSelectElement | null =
          document.querySelector("#document-sdk-type");

        if (
          filesToUpload !== null &&
          documentType !== null &&
          filesToUpload.length > 0
        ) {
          const [body] = Array.from(filesToUpload);

          try {
            const uploadLink = await fetchUploadLink(
              config,
              documentType.options[documentType.selectedIndex]
                .value as UploadDocumentType
            );
            if (uploadLink !== undefined) {
              try {
                const uploadStatus = await fetch(uploadLink, {
                  method: "PUT",
                  body,
                });

                if (uploadStatus.ok && uploadStatus.status === 200) {
                  // wait for the status of the upload to be updated
                  // start spinner
                  const overlay = document.querySelector(
                    ".document-onUpload-overlay"
                  );
                  const spanner = document.querySelector(
                    ".document-onUpload-spanner"
                  );
                  if (overlay && spanner) {
                    overlay.setAttribute("class", "show");
                    spanner.setAttribute("class", "show");
                  }
                  await poll(
                    UPLOAD_REFRESH_RETRY,
                    "PENDING",
                    config,
                    uploadLink
                  );
                  // stop spinner
                  if (overlay && spanner) {
                    overlay.classList.remove("show");
                    spanner.classList.remove("show");
                  }
                  // clear FileList
                  fileUploadElement.value = "";
                }
              } catch (error) {
                mapError(error, onError);
                fileUploadElement.value = "";
              }
            }
            // Cache the current reference
            const targetElement = reloadedElements;

            targetElement.innerHTML = "";
            const newDom = await reloadDom(config);

            if (newDom) {
              targetElement.innerHTML = newDom.innerHTML;
            }
          } catch (error) {
            mapError(error, onError);
          }
        } else {
          mapError(
            generateGenericDocumentUploadError(
              "Unable to get file or document-sdk-type"
            ),
            onError
          );
          fileUploadElement.value = "";
        }
      });
      innerDom.append(stagnantElements);
    }

    return innerDom;
  }
  return undefined;
};
